/**
 * npm i vue@3.5.26
 * @import {BundleMatchJsonData, BundleMatchGridItem, ShopifyOptionWithValues, BundleMatchMatchesHistoryItem, ShopifyCart} from "./section-bundle-match"
 */

import { computed, createApp, ref, onMounted, onBeforeMount, watch, onUnmounted } from 'vue'

/**
 * @param {HTMLElement} mountElement
 * @param {BundleMatchJsonData} jsonData
 */
function createVueComponent(mountElement, jsonData) {
  const app = createApp({
    delimiters: ['${', '}'],
    setup() {
      const sectionSettings = ref(jsonData.settings);
      const gridItems = ref(jsonData.items);

      // ============ 响应式数据 ============
      // 购物车按钮角标
      const cartCountElement = ref(null);
      // 加购按钮
      const addToCartButtonElement = ref(null);
      // 导航栏的购物车计数元素，用于监听侧边购物车数量变化
      const headerCartCountElement = ref(document.querySelector('[data-cart-count]'));

      let headerCartCountElementObserver = null;

      /** 当前正在搭配的产品 
       * @type {import('vue').Ref<BundleMatchGridItem|null>}
       */
      const currentGridItem = ref(jsonData.items[0]);

      /**
       * 已搭配的记录
       * @type {import('vue').Ref<BundleMatchMatchesHistoryItem[]>}
       */
      const matchesHistory = ref([]);

      /** 购物车 
       * @type {import('vue').Ref<ShopifyCart | null>}
       * */
      const cart = ref(null);

      /** 购物车计数
       * @type {import('vue').Ref<string | null>}
       * */
      const cartCount = ref(0);

      /** 正在加购中 */
      const addToCartLoading = ref(false);

      /** 最后匹配完成的弹窗 */
      const finalMatchPopup = ref(false);

      // ============ 计算属性 ============

      /** 产品卡片的产品变体，优先级：用户已选择过的变体 > 后台指定的初始变体 > 首个可买变体或首个变体 */
      const gridItemVariant = computed(() => {
        /** 
         * @param {BundleMatchGridItem} gridItem 
         * @returns {BundleMatchGridItem['variants'][0]|undefined}
         */
        return (gridItem) => {
          if (!gridItem) return undefined;
          const productId = gridItem.product.id;
          // 后台设置的初始变体ID，处理空字符串
          const settingsInitialVariantId = (gridItem.settings.initial_variant ?? '').trim() == ''
            ? undefined
            : gridItem.settings.initial_variant;
          const findVariantId = matchesHistory.value.find(v => v.productId == productId)?.variantId
            ?? settingsInitialVariantId
            ?? gridItem.product?.first_available_variant_id;
          return (findVariantId != undefined && findVariantId != '')
            ? gridItem.variants.find(v => v.variant_object.id == findVariantId)
            : undefined;
        };
      });

      /** 当前产品卡片的产品变体 */
      const currentGridItemVariant = computed(() => {
        if (!currentGridItem.value) return undefined;
        return gridItemVariant.value(currentGridItem.value);
      });

      /** 下拉选项按钮的文本 */
      const dropdownOptionButtonLabel = computed(() => {
        /** 
         * @param {BundleMatchGridItem} gridItem 
         * @param {ShopifyOptionWithValues} option
         * @returns {string}
         */
        return (gridItem, option) => {
          const productId = gridItem?.product?.id;
          const matchesHistoryItem = matchesHistory.value.find(v => v.productId == productId);
          // 这个 Option 用户选择过选项值
          const hasCostomerSelected = matchesHistoryItem && matchesHistoryItem.selectedOptionsPosition.includes(option.position);

          if (hasCostomerSelected) {
            // 匹配过，用已匹配的变体的对应 option value 作为展示名称
            const findOption = gridItem.product.options_with_values.find(_option => _option.name == option.name);
            const findVariant = gridItem.variants.find(v => v.variant_object.id == matchesHistoryItem.variantId);
            const findOptionValue = findVariant?.variant_object.options[findOption.position - 1];
            return findOptionValue || option.name;
          }
          // 未匹配过，用 option name 作为展示名称
          return option.name;
        };
      });

      /**
       * Option Value 是否已选中
       */
      const optionValueIsSelected = computed(() => {
        /** 
         * @param {BundleMatchGridItem} gridItem 
         * @param {number} optionPosition
         * @param {string} optionValue
         * @returns {boolean}
         */
        return (gridItem, optionPosition, optionValueName) => {
          const variant = gridItemVariant.value(gridItem);
          const findOptionValue = variant?.variant_object?.options?.[optionPosition - 1];
          return findOptionValue == optionValueName;
        };
      });

      /**
       * Option Value 是否售罄、是否需要隐藏
       * soldOut: 售罄
       * hidden: 隐藏
       */
      const optionValueAvailable = computed(() => {
        /** 
         * @param {BundleMatchGridItem} gridItem 
         * @param {number} optionPosition
         * @param {string} optionValue
         * @returns {{soldOut: boolean, hidden: boolean}}
         */
        return (gridItem, optionPosition, optionValue) => {
          const variant = gridItemVariant.value(gridItem);
          let newOptionValues = [...variant.variant_object.options];
          newOptionValues[optionPosition - 1] = optionValue;
          // 用新的选项值，尝试匹配是否存在对应选项的产品变体
          const findNewVariant = gridItem.variants.find(v => v.variant_object.options.toString() == newOptionValues.toString());
          return {
            soldOut: !(findNewVariant?.variant_object.available ?? true),
            hidden: !findNewVariant
          };
        };
      });

      /** Option 是否为颜色选择 */
      const optionIsColor = computed(() => {
        /** @param {string} name */
        return (name) => {
          return name.toLowerCase().trim() == 'color';
        };
      });

      /** 展示产品图片，首张为PC端，第二张为M端 */
      const gridItemfeaturedImage = computed(() => {
        /** 
         * @param {BundleMatchGridItem} gridItem 
         * @returns {ShopifyImage}
         */
        return (gridItem) => {
          const variant = gridItemVariant.value(gridItem);
          return variant?.addition?.display_image?.length ? variant?.addition?.display_image : [variant?.variant_object?.featured_image];
        };
      });

      /**
       * 默认/初始变体
       */
      const defaultVariant = computed(() => {
        /** 
         * @param {number} productId 
         * @returns {string|undefined}
         */
        return (productId) => {
          const item = gridItems.value.find(item => item.product.id == productId);
          return item?.settings?.initial_variant;
        };
      });

      // 当前变体、选项对应的线描图
      const lineDrawingImage = computed(() => {
        // 遍历当前产品的 Option + 当前对应变体的 Option values
        for (const option of currentGridItem.value.product.options_with_values) {
          const optionValue = currentGridItemVariant.value.variant_object.options[option.position - 1];

          // 根据 option name + option value 匹配对应的线描图，匹配不到时默认用首张线描图
          const findLineDrawing = currentGridItem.value.settings.line_drawing_list.find(lineDrawing =>
            lineDrawing.option.name != null && lineDrawing.option.value != null
            && lineDrawing.option.name.trim().toLowerCase() == option.name.trim().toLowerCase()
            && lineDrawing.option.value.trim().toLowerCase() == optionValue.trim().toLowerCase()
          ) ?? currentGridItem.value.settings.line_drawing_list[0];
          if (findLineDrawing) {
            return findLineDrawing.image;
          }
        }
        return undefined;
      });

      // 字符串转换 handle 形式
      const stringToHandle = computed(() => {
        /** @param {string} str */
        return (str) => {
          if (typeof str !== 'string') return '';
          // 转换为小写
          let result = str.toLowerCase();
          // 替换特殊字符和空白字符为连字符
          result = result.replace(/[^a-z0-9]+/g, '-');
          // 移除开头的连字符
          result = result.replace(/^-+/, '');
          // 移除结尾的连字符
          result = result.replace(/-+$/, '');
          return result;
        };
      });

      // ============ 方法 ============

      const init = () => {
        gridItems.value = jsonData.items;
        sectionSettings.value = jsonData.settings;
        fetchCart();
      };

      /**
       * @param {boolean} doAnimate 是否播放小球投篮动画
       */
      const fetchCart = async (doAnimate = false) => {
        try {
          const res = await fetch(window.Shopify.routes.root + 'cart.json');
          const cartData = await res.json();
          cart.value = cartData;
          if (doAnimate) {
            await doAnimateBallToCart(addToCartButtonElement.value, cartCountElement.value);
          }
          // 购物车统计小标
          $('[data-cart-count]').html(cart.value.items.reduce((prev, curr) => prev + curr.quantity, 0) ?? 0);
          // 变更计数
          cartCount.value = cart.value.items.reduce((prev, curr) => prev + curr.quantity, 0) ?? 0;
        } catch (error) {
          T4SThemeSP.Notices('Failed to fetch cart!', 'error');
          console.error('Failed to fetch cart:', error);
        }
      };

      /**
       * 加购
       * @param {{id: number, quantity: number}[]} items 
       */
      const addToCart = async (items) => {
        try {
          const formData = {
            items: items
          };
          const res = await fetch(window.Shopify.routes.root + 'cart/add.js', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json'
            },
            body: JSON.stringify(formData),
          });
          let content = {};
          try {
            content = await res.json();
          } catch (error) {
            // ignore
          }
          if (res.status == 422) {
            T4SThemeSP.Notices(content?.message);
            return false;
          } else if (res.status == 429) {
            T4SThemeSP.Notices('To many request!')
            return false;
          } else if (res.status != 200) {
            T4SThemeSP.Notices(content?.message);
            return false;
          }
          return true;
        } catch (error) {
          T4SThemeSP.Notices('Add to cart failed!', 'error')
          console.error(error);
          return false;
        }
      };

      /**
       * 价格格式化
       * @param {number | string} price 
       * @returns {string}
       */
      const formatMoney = (price) => {
        // return T4SThemeSP?.Currency?.formatMoney(price, T4Sconfigs.moneyFormatWithoutCurrency)
        // 复制主题的代码，T4SThemeSP.Currency 初始化太慢了
        const str = window.T4Sconfigs?.moneyWithoutCurrencyFormat ?? '${{amount}}';
        const a = price;
        const o = str;

        var t = str,
          e = undefined,
          n = function (t, e) {
            return null == t || t != t ? e : t
          };
        "string" == typeof a && (a = a.replace(".", ""));
        var s = ""
          , r = /\{\{\s*(\w+)\s*\}\}/
          , d = o || t,
          n = function (t, e) {
            return null == t || t != t ? e : t
          };
        function l(t, e, i, a) {
          if (e = n(e, 2),
            i = n(i, ","),
            a = n(a, "."),
            isNaN(t) || null == t)
            return 0;
          var o = (t = (t / 100).toFixed(e)).split(".");
          return o[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, "$1" + i) + (o[1] ? a + o[1] : "")
        }
        switch (d.match(r)[1]) {
          case "amount":
            s = l(a, 2),
              e && s && s.includes(".") && (s = s.replace(".", "<sup>") + "</sup>");
            break;
          case "amount_no_decimals":
            s = l(a, 0);
            break;
          case "amount_with_comma_separator":
            s = l(a, 2, ".", ","),
              e && s && s.includes(".") && (s = s.replace(",", "<sup>") + "</sup>");
            break;
          case "amount_no_decimals_with_comma_separator":
            s = l(a, 0, ".", ",");
            break;
          case "amount_no_decimals_with_space_separator":
            s = l(a, 0, " ")
        }
        return d.replace(r, s);
      }

      /**
       * 产品卡片点击事件
       * @param {BundleMatchGridItem} item 
       */
      const onClickGridItem = (item) => {
        currentGridItem.value = item;
      };

      /** 
       * 选择了选项值
       * @param {ShopifyOptionWithValues} option
       * @param {ShopifyOptionWithValues['values'][0]} optionValue
       */
      const onClickOptionValue = (e, option, optionValue) => {
        const currentItem = currentGridItem.value;
        const currentVariant = currentGridItemVariant.value;
        if (!currentItem || !currentVariant) {
          console.error('No current item or variant selected');
          return;
        }
        // 根据点击的选项值，组合新的选项搭配
        const cacheOptionValues = currentVariant.variant_object.options.map((value, index) => {
          const position = index + 1;
          return position == option.position ? optionValue.name : value;
        });
        // 匹配新对应的变体
        let findVariant = currentItem.variants.find(variant =>
          variant.variant_object.options.every((variantOptionValue, index) =>
            cacheOptionValues[index] == variantOptionValue
          )
        );
        if (!findVariant) {
          // 匹配不到对应的变体时，只按照选择选项值，模糊匹配其他变体
          findVariant = currentItem.variants.find(variant =>
            variant.variant_object.options[option.position - 1] == optionValue.name
          );
        }
        if (!findVariant) {
          // 这还找不到那就是有问题了
          console.error('onClickOptionValue: variant not found');
          return;
        }
        // 切换变体
        handleChangeVariant(currentItem.product.id, findVariant.variant_object.id, option);

        // 收起下拉框
        const dropdown = e.target.closest('component-bundle-match-dropdown');
        if (dropdown) {
          dropdown.close();
        }
      };

      /**
       * 记录历史
       * @param {number} productId 
       * @param {number} variantId 
       * @param {ShopifyOptionWithValues | undefined} option
       */
      const handleAddOrUpdateVariantToHistory = (productId, variantId, option) => {
        const findHistory = matchesHistory.value.find(item => item.productId == productId);
        if (findHistory) {
          if (option && !findHistory.selectedOptionsPosition.includes(option.position)) {
            findHistory.selectedOptionsPosition.push(option.position);
          }
          findHistory.variantId = variantId;
        } else {
          matchesHistory.value.push({
            selectedOptionsPosition: option ? [option.position] : [],
            productId: productId,
            variantId: variantId,
          });
        }
      };

      /**
       * 切换变体，并记录历史
       * @param {number} productId 
       * @param {number} variantId 
       * @param {ShopifyOptionWithValues} option
       */
      const handleChangeVariant = (productId, variantId, option) => {
        handleAddOrUpdateVariantToHistory(productId, variantId, option);
      };

      /**
       * 数量减一
       */
      const onClickQuantityMinus = () => {
        currentGridItem.value.quantity > 1 ? currentGridItem.value.quantity-- : (currentGridItem.value.quantity = 1);
      }

      /**
       * 数量加一
       */
      const onClickQuantityPlus = () => {
        if (currentGridItemVariant.value.addition.inventory_quantity <= 1 || currentGridItem.value.quantity == undefined) {
          currentGridItem.value.quantity = 1;
        } else if (currentGridItem.value.quantity < currentGridItemVariant.value.addition.inventory_quantity) {
          currentGridItem.value.quantity++;
        } else {
          currentGridItem.value.quantity = currentGridItemVariant.value.addition.inventory_quantity;
        }
      }

      /**
       * quantity 数量变化
       */
      const onChangeQuantity = () => {
        const quantity = currentGridItem.value.quantity;
        if (quantity < 1) {
          currentGridItem.value.quantity = 1;
        } else if (quantity > currentGridItemVariant.value.addition.inventory_quantity) {
          currentGridItem.value.quantity = currentGridItemVariant.value.addition.inventory_quantity;
        }
      }

      /**
       * 点击购物车
       */
      const onClickCart = () => {
        // document.querySelector('.t4s-site-nav__cart > a').click();
        // $(document.querySelector('.t4s-site-nav__cart > a')).click();
        // finalMatchPopup.value = true;
      };

      /**
       * 点击加入购物车
       */
      const onClickAddToCart = async (e) => {
        // 先加购
        addToCartLoading.value = true;
        try {
          const result = await addToCart([{
            id: currentGridItemVariant.value.variant_object.id,
            quantity: currentGridItem.value.quantity,
          }]);
          if (result) {
            // 刷新侧边购物车
            T4SThemeSP.Cart.getToFetch();
            // 记录历史
            handleAddOrUpdateVariantToHistory(currentGridItem.value.product.id, currentGridItemVariant.value.variant_object.id);
            // 小球投篮动画
            await doAnimateBallToCart(addToCartButtonElement.value, cartCountElement.value);
            // 刷新该模块的购物车
            await fetchCart();
            const remainGridItems = gridItems.value.filter(item => matchesHistory.value.every(historyItem => historyItem.productId != item.product.id));
            if (remainGridItems.length > 0) {
              // 跳转下一个产品
              currentGridItem.value = remainGridItems[0];
            } else {
              // 弹窗
              // finalMatchPopup.value = true;
            }
          }
          addToCartLoading.value = false;
        } catch (error) {
          addToCartLoading.value = false;
        }
      };

      /**
       * 点击弹窗按钮2
       */
      const onClickDialogButton2 = (e) => {
        currentGridItem.value = gridItems.value[0];
        finalMatchPopup.value = false;
      };

      /**
       * 播放动画效果：小球投入购物车
       * @param {HTMLElement} fromTarget 小球起点元素
       * @param {HTMLElement} toTarget 小球终点元素
       */
      async function doAnimateBallToCart(fromTarget, toTarget) {
        // 获取按钮位置
        const btnRect = fromTarget.getBoundingClientRect();
        const cartRect = toTarget.getBoundingClientRect();

        // 计算起始点
        const startX = btnRect.left + btnRect.width / 2;
        const startY = btnRect.top;

        // 计算终点
        const endX = cartRect.left + cartRect.width / 2;
        const endY = cartRect.top;

        // 创建动画
        await createBasketballAnimation(startX, startY, endX, endY);
      };

      /**
       * 创建小球投篮动画
       * @param {number} startX 
       * @param {number} startY 
       * @param {number} endX 
       * @param {number} endY 
       * @returns { success: boolean, error?: string }
       */
      async function createBasketballAnimation(startX, startY, endX, endY, duration = 500) {
        try {
          // 创建小球
          const ball = document.createElement('div');
          ball.className = 'ball';
          document.body.appendChild(ball);

          // 设置起始位置
          ball.style.left = startX + 'px';
          ball.style.top = startY + 'px';

          // 计算控制点，创建半圆形/椭圆形轨迹
          const controlX = (startX + endX) / 2;
          const controlY = Math.min(startY, endY) - 100;

          // 动画参数
          const startTime = performance.now();

          // 使用async/await处理动画循环
          await new Promise((resolve) => {
            function animate(currentTime) {
              const elapsed = currentTime - startTime;
              const progress = Math.min(elapsed / duration, 1);

              // 使用二次贝塞尔曲线计算当前位置
              const x = (1 - progress) * (1 - progress) * startX +
                2 * (1 - progress) * progress * controlX +
                progress * progress * endX;

              const y = (1 - progress) * (1 - progress) * startY +
                2 * (1 - progress) * progress * controlY +
                progress * progress * endY;

              // 更新小球位置
              ball.style.left = x + 'px';
              ball.style.top = y + 'px';

              // 小球大小变化（先变大后变小）
              let size;
              if (progress < 0.5) {
                // 前半段：由小变大
                size = 12 + (progress * 2) * 10;
              } else {
                // 后半段：由大变小
                size = 22 - ((progress - 0.5) * 2) * 15;
              }
              ball.style.width = size + 'px';
              ball.style.height = size + 'px';

              // 继续动画或完成
              if (progress < 1) {
                requestAnimationFrame(animate);
              } else {
                resolve();
              }
            }

            requestAnimationFrame(animate);
          });

          // 清理动画元素
          document.body.removeChild(ball);

          // 添加购物车弹跳效果
          await animateCartBounce(duration);

          return { success: true };
        } catch (error) {
          console.error('动画执行出错:', error);
          return { success: false, error: error.message };
        }
      }

      // 购物车弹跳动画
      async function animateCartBounce(duration = 500) {
        return new Promise(resolve => {
          cartCountElement.value.classList.add('cart-bounce');

          setTimeout(() => {
            cartCountElement.value.classList.remove('cart-bounce');
            resolve();
          }, duration);
        });
      }

      /**
       * 为URL添加或更新查询参数
       * @param {string} url - 原始URL
       * @param {Object} props - 要添加的属性对象
       * @returns {string} 更新后的URL
       */
      function urlSetProps(url, props = {}) {
        // 直接使用字符串操作，不依赖URL API
        // 分离哈希部分
        const hashIndex = url.indexOf('#');
        const hash = hashIndex > -1 ? url.slice(hashIndex) : '';
        const urlWithoutHash = hashIndex > -1 ? url.slice(0, hashIndex) : url;

        // 分离查询字符串
        const queryIndex = urlWithoutHash.indexOf('?');
        const baseUrl = queryIndex > -1 ? urlWithoutHash.slice(0, queryIndex) : urlWithoutHash;
        const queryString = queryIndex > -1 ? urlWithoutHash.slice(queryIndex + 1) : '';

        // 更新查询参数
        const params = new URLSearchParams(queryString);
        Object.entries(props).forEach(([key, value]) => {
          if (value != null) params.set(key, value);
        });

        // 构建结果
        const newQueryString = params.toString();
        let result = baseUrl;
        if (newQueryString) result += '?' + newQueryString;
        if (hash) result += hash;

        return result;
      }

      // ============ 侦听器 ============
      // 监听切换变体，避免 quantity input 的值超过库存
      watch(currentGridItemVariant, async (newVariant, oldVariant) => {
        if (newVariant != oldVariant && currentGridItem.value.quantity > currentGridItemVariant.value.addition.inventory_quantity
        ) {
          currentGridItem.value.quantity = currentGridItemVariant.value.addition.inventory_quantity;
        }
      })

      // ============ 生命周期 ============
      onBeforeMount(() => {
        headerCartCountElement.value = document.querySelector('[data-cart-count]');
        init();
      });

      onMounted(() => {
        mountElement?.classList.remove('loading');

        if (headerCartCountElement.value) {
          headerCartCountElementObserver = new MutationObserver((_) => {
            cartCount.value = headerCartCountElement.value.innerHTML?.trim();
          });
          // 开始观察
          headerCartCountElementObserver.observe(headerCartCountElement.value, {
            childList: true,        // 观察子节点的添加或删除
            subtree: true,          // 观察所有后代节点
            characterData: true,   // 观察文本内容变化
            attributes: false,      // 观察属性变化
            characterDataOldValue: false
          });
        }
      });

      onUnmounted(() => {
        // 结束观察
        if (headerCartCountElementObserver) {
          headerCartCountElementObserver.disconnect()
        }
      });


      // ============ 返回值 ============
      // 返回所有需要在模板中使用的响应式数据和方法
      return {
        // 响应式数据
        sectionSettings,
        gridItems,
        cartCountElement,
        addToCartButtonElement,
        headerCartCountElement,
        currentGridItem,
        matchesHistory,
        cart,
        cartCount,
        addToCartLoading,
        finalMatchPopup,

        // 计算属性
        currentGridItemVariant,
        gridItemVariant,
        dropdownOptionButtonLabel,
        optionValueIsSelected,
        optionValueAvailable,
        optionIsColor,
        gridItemfeaturedImage,
        defaultVariant,
        lineDrawingImage,
        stringToHandle,

        // 方法
        init,
        fetchCart,
        formatMoney,
        onClickGridItem,
        onClickOptionValue,
        handleChangeVariant,
        onClickQuantityMinus,
        onClickQuantityPlus,
        onChangeQuantity,
        onClickCart,
        onClickAddToCart,
        onClickDialogButton2,
        urlSetProps,
      };
    },
    compilerOptions: {
      isCustomElement: (tag) => ['component-bundle-match-dropdown'].includes(tag),
    }
  });

  app.mount(mountElement);
  return app;
}

// 简单的下拉框组件
if (!customElements.get('component-bundle-match-dropdown')) {
  customElements.define('component-bundle-match-dropdown', class BundleMatchDropdown extends HTMLElement {
    constructor() {
      super();
      this._onDocumentClick = this.onDocumentClick.bind(this);
      this._onButtonClick = this.onButtonClick.bind(this);
    }

    connectedCallback() {
      this.button = this.querySelector('button');
      this.dropdownWrapper = this.querySelector('[data-dropdown-wrapper]');
      document.addEventListener('click', this._onDocumentClick);
      this.button.addEventListener('click', this._onButtonClick);
    }

    disconnectedCallback() {
      document.removeEventListener('click', this._onDocumentClick);
      this.button.removeEventListener('click', this._onButtonClick);
    }

    open() {
      this.button.classList.add('is--clicked');
      this.dropdownWrapper.classList.add('is--opened');
    }

    close() {
      this.button.classList.remove('is--clicked');
      this.dropdownWrapper.classList.remove('is--opened');
    }

    toggle() {
      if (this.button.classList.contains('is--clicked')) {
        this.button.classList.remove('is--clicked');
      } else {
        this.button.classList.add('is--clicked');
      }
      if (this.dropdownWrapper.classList.contains('is--opened')) {
        this.dropdownWrapper.classList.remove('is--opened');
      } else {
        this.dropdownWrapper.classList.add('is--opened');
      }
    }

    /**
     * @param {MouseEvent} e 
     */
    onButtonClick(e) {
      this.toggle();
    }

    /**
     * @param {MouseEvent} e 
     */
    onDocumentClick(e) {
      const el = e.target.closest('.t4s-dropdown');
      const allDropdown = document.querySelectorAll('.t4s-dropdown');
      if (el) {
        for (const dropdown of allDropdown) {
          if (dropdown == el) continue;
          dropdown.firstElementChild?.classList.remove('is--clicked');
          dropdown.lastElementChild?.classList.remove('is--opened');
        }
      } else {
        for (const dropdown of allDropdown) {
          dropdown.firstElementChild?.classList.remove('is--clicked');
          dropdown.lastElementChild?.classList.remove('is--opened');
        }
      }
    }
  });
}

if (!customElements.get('bundle-match')) {
  customElements.define('bundle-match', class BundleMatch extends HTMLElement {
    constructor() {
      super();
      this.createVue();
    }

    createVue() {
      /** @type {BundleMatchJsonData} */
      const jsonData = JSON.parse(this.querySelector('[data-section-json]').innerHTML.replaceAll('},]', '}]'));

      console.log('jsonData: ', jsonData);

      const mountElement = this.querySelector('[data-vue-render]');
      if (mountElement) {
        createVueComponent(mountElement, jsonData);
      }
    }
  });
} 